import { createApiClient, type ApiClient, type ApiClientOptions } from "../api/client.ts";
import { ApiError, reportError } from "../api/errors.ts";
import type { components } from "../api/schema";
import type { TelegramLoginData } from "../telegram/telegramLogin.ts";

type AuthResponse = components["schemas"]["AuthResponse"];

export type SessionUser = AuthResponse["user"];

export type Credentials = components["schemas"]["LoginRequest"];

export type Registration = components["schemas"]["RegisterRequest"];

/** В профиле меняется только отображаемое имя; язык и тема в MVP не выводятся. */
export type ProfileChanges = Pick<components["schemas"]["UpdateMeRequest"], "displayName">;

/**
 * `restoring` — идёт первый `/auth/refresh` при старте;
 * `anonymous` — сессии не было;
 * `ended` — сессия была и закончилась (refresh отклонён или выход).
 */
export type SessionStatus = "restoring" | "authenticated" | "anonymous" | "ended";

export type SessionState = { status: SessionStatus; user: SessionUser | null };

export type Session = {
  getState: () => SessionState;
  subscribe: (listener: () => void) => () => void;
  /** Первый refresh при старте; повторные вызовы ничего не делают. */
  restore: () => void;
  login: (credentials: Credentials) => Promise<void>;
  /** Создаёт аккаунт и сразу входит в него. */
  register: (registration: Registration) => Promise<void>;
  /** Входит по данным окна Telegram; аккаунт без email создаётся при первом входе. */
  loginWithTelegram: (data: TelegramLoginData) => Promise<void>;
  logout: () => Promise<void>;
  /** Меняет профиль (`PATCH /v1/users/me`); ответ backend'а сразу становится текущим пользователем. */
  updateProfile: (changes: ProfileChanges) => Promise<void>;
  /** API-клиент, который подставляет access token и переживает его истечение. */
  client: ApiClient;
};

// На эти маршруты 401 означает ответ по существу, а не истёкший access token.
const AUTH_PATHS = ["/v1/auth/login", "/v1/auth/register", "/v1/auth/telegram", "/v1/auth/refresh", "/v1/auth/logout"];

// Коды 401, которые значат «access token не подошёл». Другой код в конверте (например,
// `invalid_device_claim_token` у привязки) — ответ по существу: refresh его не исправит.
const ACCESS_TOKEN_REJECTED = new Set(["not_authenticated", "invalid_access_token"]);

// Refresh-cookie нет, она истекла, отозвана или уже погашена ротацией. Остальные ошибки (429, 5xx, сеть) временные.
const REFRESH_REJECTED = new Set([401, 403]);

/**
 * Access token живёт только в памяти, refresh — в httpOnly cookie (ADR-004).
 * На 401 все параллельные запросы ждут один общий refresh и затем повторяются.
 */
export function createSession({ baseUrl, fetch }: ApiClientOptions): Session {
  const publicClient = createApiClient({ baseUrl, fetch });
  const listeners = new Set<() => void>();
  let state: SessionState = { status: "restoring", user: null };
  let accessToken: string | null = null;
  let refreshing: Promise<boolean> | null = null;
  // Растёт при каждом входе и выходе: ответ refresh, начатого до них, уже не должен менять сессию.
  let generation = 0;
  let restoreStarted = false;

  function setState(next: SessionState): void {
    state = next;
    for (const listener of [...listeners]) listener();
  }

  function signIn(response: AuthResponse): void {
    generation += 1;
    accessToken = response.accessToken;
    setState({ status: "authenticated", user: response.user });
  }

  /** Ответ входа (по email или через Telegram) или регистрации: с данными — сессия начата, иначе — ошибка API. */
  function signInWith({ data, error, response }: { data?: AuthResponse; error?: unknown; response: Response }): void {
    if (!data) throw ApiError.from(response, error);
    signIn(data);
  }

  function signOut(): void {
    generation += 1;
    accessToken = null;
    setState({ status: state.status === "authenticated" ? "ended" : "anonymous", user: null });
  }

  /**
   * Single-flight: пока refresh идёт, все вызовы получают один и тот же промис.
   * `false` — сервер отклонил refresh и сессия закончена; временная ошибка отклоняет промис, сессия остаётся.
   */
  function refresh(): Promise<boolean> {
    const startedAt = generation;
    refreshing ??= publicClient
      .POST("/v1/auth/refresh")
      .then(({ data, error, response }) => {
        if (generation !== startedAt) return accessToken !== null;
        if (data) {
          signIn(data);
          return true;
        }
        if (!REFRESH_REJECTED.has(response.status)) throw ApiError.from(response, error);
        signOut();
        return false;
      })
      .finally(() => {
        refreshing = null;
      });
    return refreshing;
  }

  async function authenticatedFetch(request: Request): Promise<Response> {
    const retry = request.clone();
    const usedToken = accessToken;
    const response = await fetch(withToken(request, usedToken));
    if (response.status !== 401 || usedToken === null || AUTH_PATHS.includes(new URL(request.url).pathname)) {
      return response;
    }
    if (!(await isAccessTokenRejection(response))) return response;
    // Если токен уже обновил другой запрос, второй refresh не нужен: старый refresh-cookie уже погашен.
    // Временная ошибка refresh отклоняет и этот запрос, но сессия с прежним токеном остаётся.
    if (accessToken === usedToken && !(await refresh())) return response;
    if (accessToken === null) return response;
    return fetch(withToken(retry, accessToken));
  }

  const client = createApiClient({ baseUrl, fetch: authenticatedFetch });

  return {
    getState: () => state,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    restore() {
      if (restoreStarted) return;
      restoreStarted = true;
      // Без ответа сервера восстановить сессию нельзя: приложение стартует без неё.
      refresh().catch((error: unknown) => {
        reportError(error);
        if (state.status === "restoring") setState({ status: "anonymous", user: null });
      });
    },
    async login(credentials) {
      signInWith(await publicClient.POST("/v1/auth/login", { body: credentials }));
    },
    async register(registration) {
      signInWith(await publicClient.POST("/v1/auth/register", { body: registration }));
    },
    async loginWithTelegram(data) {
      signInWith(await publicClient.POST("/v1/auth/telegram", { body: data }));
    },
    async logout() {
      try {
        await publicClient.POST("/v1/auth/logout");
      } finally {
        signOut();
      }
    },
    async updateProfile(changes) {
      const { data, error, response } = await client.PATCH("/v1/users/me", { body: changes });
      if (!data) throw ApiError.from(response, error);
      // Пока шёл запрос, из аккаунта могли выйти: чужой ответ не должен вернуть пользователя в сессию.
      if (state.user?.id === data.id) setState({ status: state.status, user: data });
    },
    client,
  };
}

/** 401 без конверта (например, от прокси) тоже считается истёкшим access token. */
async function isAccessTokenRejection(response: Response): Promise<boolean> {
  const body: unknown = await response
    .clone()
    .json()
    .catch(() => null);
  const code = isRecord(body) && isRecord(body.error) ? body.error.code : undefined;
  return typeof code !== "string" || ACCESS_TOKEN_REJECTED.has(code);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function withToken(request: Request, token: string | null): Request {
  if (token === null) return request;
  const headers = new Headers(request.headers);
  headers.set("Authorization", `Bearer ${token}`);
  return new Request(request, { headers });
}
